import { AppBrandProvider, UserType } from '@vivaldis/common'
import { useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary, bugsnagClient } from '@vivaldis/web-bugsnag'
import {
  initBugsnagTracking,
  initGoogleTagManagerTracking
} from '@vivaldis/tracking'
import {
  DrawerProvider,
  initFocusHandler,
  UnknownErrorPage
} from '@vivaldis/web-ui'
import './i18n'
import 'antd/dist/reset.css'
import './index.css'
import './config/axios'
import { App as AntdApp } from '@vivaldis/antd-ui'
import { App as AppContainer } from '@vivaldis/app'
import storage from 'localforage'
import {
  ProgressBarProvider,
  SideSheets,
  ScreenTitleProvider
} from '@vivaldis/web-ui'
import { ConfigProvider } from './providers/ConfigProvider'
import { OnboardingStepProvider } from './providers/OnboardingStepProvider'
import reportWebVitals from './reportWebVitals'
import { createReduxStore } from './config/redux'
import { reducers } from './reducers'
import { sagas } from './sagas'
import { createApolloClient } from './config/apolloClient'
import { IntercomMessengerProvider } from './app/IntercomMessenger/providers/IntercomMessengerProvider'
import { UserDashboardQueryProvider } from './providers/UserDashboardQueryProvider'
import { CompanyInformationQueryProvider } from './providers/CompanyInformationQueryProvider'
import { App as AppContent } from './app'
import { Routes } from './screens/routes'
import { StartUpDocumentsBadgesProvider } from './providers/StartUpDocumentsBadgesProvider'
import { ThemeProvider } from './providers/ThemeProvider'
import { TimesheetBadgesProvider } from './providers/TimesheetBadgesProvider'
import { AppBrandI18nSync } from './providers/AppBrandI18nSync'
import { AppBrandUserSync } from './providers/AppBrandUserSync'

initBugsnagTracking(bugsnagClient)
initGoogleTagManagerTracking()
initFocusHandler()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
})

const App = () => {
  const { apolloClient, apolloCachePersistor, store, storePersistor } =
    useMemo(() => {
      const { apolloClient, apolloCachePersistor } = createApolloClient()
      const { store, persistor: storePersistor } = createReduxStore(
        reducers,
        sagas
      )
      return {
        apolloClient,
        apolloCachePersistor,
        store,
        storePersistor
      }
    }, [])

  return (
    <AppBrandProvider>
      <ThemeProvider>
        <ConfigProvider>
          <AntdApp>
            <ProgressBarProvider>
              <ScreenTitleProvider>
                <AppBrandI18nSync />
                <AppContainer
                  apolloClient={apolloClient}
                  apolloCachePersistor={apolloCachePersistor}
                  reduxStore={store}
                  reduxPersistor={storePersistor}
                  storage={storage}
                  version={`${import.meta.env.VITE_VERSION}`}
                  migrateBefore={async () => {}}
                  migrateAfter={async () => {}}
                  userType={UserType.User}
                  sideSheets={
                    <SideSheets>
                      <Routes />
                    </SideSheets>
                  }
                  Providers={children => (
                    <QueryClientProvider client={queryClient}>
                      <AppBrandUserSync />
                      <OnboardingStepProvider>
                        <DrawerProvider>
                          <IntercomMessengerProvider>
                            <UserDashboardQueryProvider>
                              <CompanyInformationQueryProvider>
                                <TimesheetBadgesProvider>
                                  <StartUpDocumentsBadgesProvider>
                                    {children}
                                  </StartUpDocumentsBadgesProvider>
                                </TimesheetBadgesProvider>
                              </CompanyInformationQueryProvider>
                            </UserDashboardQueryProvider>
                          </IntercomMessengerProvider>
                        </DrawerProvider>
                      </OnboardingStepProvider>
                    </QueryClientProvider>
                  )}
                >
                  <AppContent />
                </AppContainer>
              </ScreenTitleProvider>
            </ProgressBarProvider>
          </AntdApp>
        </ConfigProvider>
      </ThemeProvider>
    </AppBrandProvider>
  )
}

const container = document.getElementById('root')
const root = createRoot(container!)
root.render(
  <ErrorBoundary FallbackComponent={UnknownErrorPage}>
    <App />
  </ErrorBoundary>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
